/**
 * ETIQUETAS INTERNAS NEATOS (F13.2) — vista previa e impresión en PDF A4
 *
 * El payload (URL versionada `/e/1/…` y forma compacta `NEO1:`) lo calcula el
 * servidor con la identidad ESTABLE (ART-… y código de lote): aquí sólo se
 * dibuja. El QR, con el mismo generador que los activos (`qr.ts`); el Code
 * 128, con las barras que manda el servidor. Imprimir = pedir el PDF y
 * descargarlo: no hay impresoras de etiquetas (ZPL) todavía.
 */
import { useState, type ReactNode } from 'react';

import { api, type EtiquetaVista, type PlantillaEtiquetas } from '../api.ts';
import { Error as ErrorCaja, fecha, Modal, Panel, useDatos } from './base.tsx';
import { CodigoQr } from './qr.tsx';

export const PLANTILLAS_ETIQUETAS: readonly (readonly [PlantillaEtiquetas, string, number, string])[] = [
  ['A4_3x8', '3 × 8 · 70 × 37 mm (24 por hoja)', 24, 'Sólo QR (el Code 128 no cabe legible).'],
  ['A4_2x7', '2 × 7 · 105 × 42 mm (14 por hoja)', 14, 'QR y Code 128.'],
];

/** Barras de Code 128 ('1' barra / '0' espacio) como SVG, con su zona de silencio. */
export function Code128({ barras, titulo, alto = 40 }: { barras: string; titulo: string; alto?: number }): ReactNode {
  const silencio = 10;
  const ancho = barras.length + silencio * 2;
  const rects: ReactNode[] = [];
  let i = 0;
  while (i < barras.length) {
    if (barras[i] !== '1') { i++; continue; }
    const ini = i;
    while (i < barras.length && barras[i] === '1') i++;
    rects.push(<rect key={ini} x={ini + silencio} y={0} width={i - ini} height={alto} />);
  }
  return (
    <svg className="codigo-barras" role="img" aria-label={titulo} viewBox={`0 0 ${String(ancho)} ${String(alto)}`} preserveAspectRatio="none"
      shapeRendering="crispEdges" style={{ width: '100%', maxWidth: ancho * 1.2, height: 48 }}>
      <rect width={ancho} height={alto} fill="#fff" />
      <g fill="#000">{rects}</g>
    </svg>
  );
}

/** La etiqueta tal y como se imprime (proporciones aproximadas). */
export function VistaEtiqueta({ v }: { v: EtiquetaVista }): ReactNode {
  //  F13.5-A: se nombra por el código de negocio; el QR/Code 128 llevan el token (la etiqueta no caduca si cambia el maestro).
  const titulo = v.lote === null ? `Etiqueta del artículo ${v.articulo.codigoNegocio}` : `Etiqueta del lote ${v.lote.codigo} de ${v.articulo.codigoNegocio}`;
  return (
    <figure className="vista-etiqueta" aria-label={titulo} style={{ border: '1px solid var(--borde, #ccc)', borderRadius: 6, padding: 12, margin: 0, maxWidth: 460, background: '#fff', color: '#000' }}>
      <div className="fila" style={{ alignItems: 'flex-start', gap: 12 }}>
        <CodigoQr texto={v.url} tamano={120} titulo={`QR: ${v.url}`} />
        <div style={{ minWidth: 0 }}>
          <div className="mono" style={{ fontWeight: 700, fontSize: '1.1rem' }}>{v.articulo.codigoNegocio}</div>
          <div>{v.articulo.descripcion}</div>
          {v.articulo.codigoNegocio !== v.articulo.codigo && <div className="pequena">NeatOS: <span className="mono">{v.articulo.codigo}</span></div>}
          {v.lote !== null && <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>Lote: <span className="mono">{v.lote.codigo}</span></div>}
          {v.lote?.fechaCaducidad != null && <div className="pequena">Cad.: {fecha(v.lote.fechaCaducidad)}</div>}
        </div>
      </div>
      {v.code128 !== null && <div style={{ marginTop: 8 }}><Code128 barras={v.code128} titulo={`Code 128: ${v.compacto}`} /></div>}
      <figcaption className="suave pequena mono" style={{ marginTop: 6, overflowWrap: 'anywhere' }}>{v.compacto}</figcaption>
    </figure>
  );
}

export function PanelEtiqueta({ ruta }: { ruta: string }): ReactNode {
  const v = useDatos(() => api.get<EtiquetaVista>(ruta), [ruta]);
  return (
    <Panel estado={v}>
      {(d) => (
        <>
          {d.avisos.map((a) => <p key={a} className="nota aviso pequena" role="status">{a}</p>)}
          <VistaEtiqueta v={d} />
          <p className="suave pequena" style={{ marginTop: 8 }}>
            La etiqueta sólo identifica el {d.lote === null ? 'artículo' : 'lote'} con su código estable de NeatOS: leerla no da acceso a nada.
            {d.code128En.length === 0 && ' El código es demasiado largo para Code 128: se imprime sólo el QR.'}
          </p>
        </>
      )}
    </Panel>
  );
}

export interface ElementoEtiqueta { readonly tipo: 'ART' | 'LOT'; readonly id: string; readonly titulo: string }

function descargarBlob(b: Blob, nombre: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(b);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Copias por etiqueta, plantilla de hoja y posición inicial (aprovechar una hoja empezada) → PDF. */
export function DialogoEtiquetas({ elementos, cerrar }: { elementos: readonly ElementoEtiqueta[]; cerrar: () => void }): ReactNode {
  const [copias, setCopias] = useState<Record<string, string>>(() => Object.fromEntries(elementos.map((e) => [e.id, '1'])));
  const [plantilla, setPlantilla] = useState<PlantillaEtiquetas>('A4_3x8');
  const [inicio, setInicio] = useState('1');
  const [bordes, setBordes] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState<number | null>(null);
  const porHoja = PLANTILLAS_ETIQUETAS.find((p) => p[0] === plantilla)![2];
  const n = (t: string): number => (/^\d+$/.test(t) ? Number(t) : NaN);
  const total = elementos.reduce((s, e) => s + (n(copias[e.id] ?? '') || 0), 0);
  const invalidas = elementos.some((e) => { const c = n(copias[e.id] ?? ''); return !(c >= 1 && c <= 500); });
  const inicioN = n(inicio);
  const inicioMal = !(inicioN >= 1 && inicioN <= porHoja);
  const hojas = inicioMal ? 0 : Math.ceil((inicioN - 1 + total) / porHoja);
  async function imprimir(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      const pdf = await api.descargar('/api/inventario/etiquetas/pdf', {
        plantilla, inicio: inicioN - 1, bordes, etiquetas: elementos.map((e) => ({ tipo: e.tipo, id: e.id, copias: n(copias[e.id]!) })),
      });
      descargarBlob(pdf, `etiquetas-neatos-${plantilla}.pdf`);
      setHecho(total);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo={elementos.length === 1 ? `Imprimir etiqueta · ${elementos[0]!.titulo}` : `Imprimir ${String(elementos.length)} etiquetas`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cerrar</button>
        <button type="button" className="principal" disabled={enviando || invalidas || inicioMal || total === 0 || total > 1000} onClick={() => void imprimir()}>
          {enviando ? 'Generando…' : 'Descargar PDF'}</button></>}>
      {error !== null && <ErrorCaja error={error} />}
      {hecho !== null && <p className="nota exito pequena" role="status">PDF generado con {hecho} etiqueta(s). Imprímelo al 100 % (sin «ajustar a la página») en la hoja adhesiva.</p>}
      <div className="tabla-desliza">
        <table aria-label="Etiquetas y copias">
          <thead><tr><th>Etiqueta</th><th className="num">Copias</th></tr></thead>
          <tbody>
            {elementos.map((e) => (
              <tr key={e.id}>
                <td>{e.titulo}</td>
                <td className="num"><input type="number" min={1} max={500} inputMode="numeric" style={{ width: 80 }} aria-label={`Copias de ${e.titulo}`}
                  value={copias[e.id] ?? ''} onChange={(ev) => setCopias({ ...copias, [e.id]: ev.target.value })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invalidas && <p className="error-campo">Entre 1 y 500 copias por etiqueta.</p>}
      <div className="form-rejilla">
        <div className="campo"><label htmlFor="etq-hoja">Hoja</label>
          <select id="etq-hoja" aria-describedby="etq-hoja-ayuda" value={plantilla} onChange={(ev) => setPlantilla(ev.target.value as PlantillaEtiquetas)}>
            {PLANTILLAS_ETIQUETAS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
          <div id="etq-hoja-ayuda" className="suave pequena">{PLANTILLAS_ETIQUETAS.find((p) => p[0] === plantilla)![3]}</div>
        </div>
        <div className="campo"><label htmlFor="etq-inicio">Empezar en la posición</label>
          <input id="etq-inicio" aria-describedby="etq-inicio-ayuda" type="number" min={1} max={porHoja} inputMode="numeric" value={inicio} onChange={(ev) => setInicio(ev.target.value)} />
          <div id="etq-inicio-ayuda" className="suave pequena">Para aprovechar una hoja empezada (1 = arriba a la izquierda).</div>
          {inicioMal && <div className="error-campo">Entre 1 y {porHoja}.</div>}
        </div>
      </div>
      <label className="fila" style={{ gap: 8, alignItems: 'center', margin: '12px 0 8px' }}>
        <input type="checkbox" checked={bordes} onChange={(ev) => setBordes(ev.target.checked)} /> Marcar el contorno (papel normal, para recortar)</label>
      <p className="pequena" role="status">{total} etiqueta(s){hojas > 0 ? ` · ${String(hojas)} hoja(s)` : ''}{total > 1000 ? ' · máximo 1000 por PDF' : ''}</p>
    </Modal>
  );
}
